/* eslint-disable @typescript-eslint/no-explicit-any */
import { extend } from '@syncfusion/ej2-base';
import { getDateFromRecurrenceDateString, getRecurrenceStringFromDate } from '../../recurrence-editor/date-generator';
/**
 * ICalendar Export Module
 */
export class ICalendarExport {
    constructor(parent) {
        this.parent = parent;
    }
    initializeCalendarExport(fileName, customData) {
        const icsString = this.getCalendarString(fileName, customData);
        this.download(icsString, fileName);
    }
    getCalendarString(fileName, customData) {
        let eventsData = (customData) ? customData :
            extend([], this.parent.eventsData, null, true);
        eventsData = this.parent.eventBase.sortByTime(eventsData);
        const SEPARATOR = (navigator.appVersion.indexOf('Win') !== -1) ? '\r\n' : '\n';
        const iCalendarEvents = [];
        const filterCollection = [];
        const timeZone = this.parent.timezone || this.parent.tzModule.getLocalTimezoneName();
        const fields = this.parent.eventFields;
        eventsData.forEach((eventObj) => {
            let uId = eventObj[fields.id] || eventObj.Guid || this.parent.eventBase.generateGuid();
            const editedExDate = [];
            if (eventObj[fields.recurrenceID]) {
                const filter = this.filterEvents(filterCollection, fields.id, eventObj[fields.recurrenceID]);
                uId = filter.length > 0 ? filter[0].UID : uId;
            }
            if (!eventObj[fields.recurrenceID] && eventObj[fields.recurrenceRule] && eventObj[fields.recurrenceException]) {
                const exDate = (eventObj[fields.recurrenceException]).split(',');
                const editedObj = this.filterEvents(eventsData, fields.recurrenceID, eventObj[fields.id]);
                editedObj.forEach((edited) => {
                    editedExDate.push(getRecurrenceStringFromDate(edited[fields.startTime]));
                });
                const exceptionDateList = exDate.filter((value) => editedExDate.indexOf(value) === -1);
                eventObj[fields.recurrenceException] = (exceptionDateList.length > 0) ? (exceptionDateList.join(',') + ',') : '';
            }
            const startZone = (eventObj[fields.startTimezone] || timeZone);
            const endZone = (eventObj[fields.endTimezone] || timeZone);
            const readonly = (eventObj[fields.isReadonly]) ? (eventObj[fields.isReadonly]) : false;
            const calendarEvent = [
                'BEGIN:VEVENT',
                'LOCATION:' + (eventObj[fields.location] || ''),
                'SUMMARY:' + (eventObj[fields.subject] || ''),
                'UID:' + uId,
                'DESCRIPTION:' + (eventObj[fields.description] || ''),
                'ISREADONLY:' + readonly,
                'END:VEVENT'
            ];
            if (eventObj[fields.isAllDay]) {
                calendarEvent.splice(4, 0, 'DTEND;VALUE=DATE:' + this.convertDateToString(eventObj[fields.endTime], true));
                calendarEvent.splice(4, 0, 'DTSTART;VALUE=DATE:' + this.convertDateToString(eventObj[fields.startTime], true));
            }
            else if (!eventObj[fields.isAllDay] && !eventObj[fields.recurrenceRule]) {
                calendarEvent.splice(4, 0, 'DTEND:' + this.convertDateToString(eventObj[fields.endTime]));
                calendarEvent.splice(4, 0, 'DTSTART:' + this.convertDateToString(eventObj[fields.startTime]));
            }
            else {
                calendarEvent.splice(4, 0, 'DTEND;TZID="' + endZone + '":' + this.convertDateToString(eventObj[fields.endTime]));
                calendarEvent.splice(4, 0, 'DTSTART;TZID="' + startZone + '":'
                    + this.convertDateToString(eventObj[fields.startTime]));
            }
            if (eventObj[fields.recurrenceRule]) {
                calendarEvent.splice(4, 0, 'RRULE:' + eventObj[fields.recurrenceRule]);
            }
            if (eventObj[fields.recurrenceException]) {
                const exDate = eventObj[fields.recurrenceException].split(',');
                for (let i = 0; i < exDate.length - 1; i++) {
                    calendarEvent.splice(5, 0, 'EXDATE:' +
                        this.convertDateToString(getDateFromRecurrenceDateString(exDate[parseInt(i.toString(), 10)]), eventObj[fields.isAllDay]));
                }
            }
            if (eventObj[fields.recurrenceID]) {
                calendarEvent.splice(4, 0, 'RECURRENCE-ID;TZID="' + startZone + '":'
                    + this.convertDateToString(eventObj[fields.startTime], eventObj[fields.isAllDay]));
            }
            const customFields = this.customFieldFilter(eventObj, fields);
            if (customFields.length > 0) {
                customFields.forEach((customField) => calendarEvent.splice(4, 0, customField + ':' + (eventObj[`${customField}`] || '')));
            }
            const app = extend({}, eventObj);
            app.UID = uId;
            filterCollection.push(app);
            iCalendarEvents.push(calendarEvent.join(SEPARATOR));
        });
        const iCalendar = [
            'BEGIN:VCALENDAR',
            'PRODID:-//Syncfusion Inc//Scheduler//EN',
            'VERSION:2.0',
            'CALSCALE:GREGORIAN',
            'METHOD:PUBLISH',
            'X-WR-CALNAME:' + (fileName || 'Calendar'),
            'X-WR-TIMEZONE:' + timeZone
        ].join(SEPARATOR);
        const icsString = iCalendar + SEPARATOR + iCalendarEvents.join(SEPARATOR) + SEPARATOR + 'END:VCALENDAR';
        return icsString;
    }
    customFieldFilter(eventObj, fields) {
        const defaultFields = Object.keys(fields).map((key) => fields[`${key}`]);
        const eventFields = Object.keys(eventObj);
        return eventFields.filter((value) => (defaultFields.indexOf(value) === -1) && (value !== 'Guid'));
    }
    convertDateToString(eventDate, allDay) {
        const year = ('0000' + (eventDate.getFullYear().toString())).slice(-4);
        const month = ('00' + ((eventDate.getMonth() + 1).toString())).slice(-2);
        const date = ('00' + ((eventDate.getDate()).toString())).slice(-2);
        const hours = ('00' + (eventDate.getHours().toString())).slice(-2);
        const minutes = ('00' + (eventDate.getMinutes().toString())).slice(-2);
        const seconds = ('00' + (eventDate.getSeconds().toString())).slice(-2);
        const timeString = (allDay) ? year + month + date : year + month + date + 'T' + hours + minutes + seconds;
        return timeString;
    }
    download(icsString, fileName) {
        const buffer = new Blob([icsString], { type: 'data:text/calendar;charset=utf8' });
        fileName = (fileName || 'Calendar') + '.ics';
        if (navigator.msSaveBlob) {
            navigator.msSaveBlob(buffer, fileName);
        }
        else {
            const downloadLink = document.createElementNS('http://www.w3.org/1999/xhtml', 'a');
            downloadLink.download = fileName;
            downloadLink.href = URL.createObjectURL(buffer);
            const event = document.createEvent('MouseEvent');
            event.initEvent('click', true, true);
            downloadLink.dispatchEvent(event);
            setTimeout(() => {
                URL.revokeObjectURL(downloadLink.href);
                downloadLink.href = undefined;
            });
        }
    }
    filterEvents(data, field, value) {
        return data.filter((e) => e[`${field}`] === value);
    }
    getModuleName() {
        return 'iCalendarExport';
    }
    destroy() {
        if (!this.parent || this.parent && this.parent.isDestroyed) {
            return;
        }
        this.parent = null;
    }
}
